import {
  GraphQLGlobalTypes,
  TableRowDataFunctions_getRowDataWithMaxCountsForTable,
  TableRowDataFunctions_getRowDataWithMaxCountsForTableWithTagFilter,
  TablesByTagQueryGql,
  TablesDefaultDirections,
  TablesDefaultSortField,
  TablesExplorerQueryGrid_QueryResultListNode,
  TablesExplorerQueryGrid_QueryResultListNodeWithTagFilter,
  TablesExplorerQueryGrid_QueryResults,
  TablesExplorerQueryGrid_QueryResultsWithTagFilter,
  TablesExplorerQueryGrid_QueryVariables,
  TablesExplorerQueryGrid_QueryVariablesWithTagFilter,
  TablesExplorerQueryGrid_selectConnectionListResult,
  TablesExplorerQueryGrid_selectConnectionListResultWithTagFilter,
  TablesExternalAssetsDisplayGridRowData,
  TablesQueryGql,
} from '@tableau/data-catalog-client-components';
import { defaultMemoize } from 'reselect';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';

import { ISliceable } from '../../httpClient/fetchers/ISliceable';
import { catalogApolloClient } from '../CatalogApolloClient';
import { BaseGraphQLResourceQuery } from './BaseGraphQLResourceQuery';
import { getExplorerGraphQLFilters } from './CatalogFilterClauseHelpers';
import { getSortOrderOrDefault } from './CatalogOrderClauseHelpers';
import { makeSliceableFactoryForGraphQLQuery } from './GraphQLSliceable';

const getDefaultSliceableFactorySingleton = defaultMemoize(() => {
  return makeSliceableFactoryForGraphQLQuery<
    TablesExplorerQueryGrid_QueryResultListNode,
    TablesExternalAssetsDisplayGridRowData,
    TablesExplorerQueryGrid_QueryVariables,
    TablesExplorerQueryGrid_QueryResults
    >(
    {
      queryGql: TablesQueryGql,
      selectConnectionListResult: TablesExplorerQueryGrid_selectConnectionListResult,
      mapResultNode: TableRowDataFunctions_getRowDataWithMaxCountsForTable,
    },
    catalogApolloClient
  );
});

const getTagFilteredSliceableFactorySingleton = defaultMemoize(() => {
  return makeSliceableFactoryForGraphQLQuery<
    TablesExplorerQueryGrid_QueryResultListNodeWithTagFilter,
    TablesExternalAssetsDisplayGridRowData,
    TablesExplorerQueryGrid_QueryVariablesWithTagFilter,
    TablesExplorerQueryGrid_QueryResultsWithTagFilter
    >(
    {
      queryGql: TablesByTagQueryGql,
      selectConnectionListResult: TablesExplorerQueryGrid_selectConnectionListResultWithTagFilter,
      mapResultNode: TableRowDataFunctions_getRowDataWithMaxCountsForTableWithTagFilter,
    },
    catalogApolloClient
  );
});

export class TablesGraphQLResourceQuery
  extends BaseGraphQLResourceQuery<TablesExternalAssetsDisplayGridRowData> {

  protected makeSliceable(): ISliceable<TablesExternalAssetsDisplayGridRowData> {
    const orderBy = getSortOrderOrDefault(this.getOrder(), TablesDefaultSortField, TablesDefaultDirections);
    const filterParameters = getExplorerGraphQLFilters(this.getFilters());

    const partialQueryVariables = {
      orderBy: orderBy as GraphQLGlobalTypes.DatabaseTableSortOrder,
      textSearch: filterParameters.search,
      hasActiveWarning: filterParameters.hasActiveWarning,
      isCertified: filterParameters.isCertified,
      isGroupFFEnabled: FeatureFlags.mergeDedupeDatabasesEnabled,
      publishedConnectionsLineageEnabled: FeatureFlags.publishedConnectionsLineageEnabled,
      columnDQWs: FeatureFlags.columnDQWsEnabled
    };

    const sliceable = filterParameters.tags
      ? getTagFilteredSliceableFactorySingleton()({ tagName: filterParameters.tags, ...partialQueryVariables })
      : getDefaultSliceableFactorySingleton()(partialQueryVariables);

    return sliceable;
  }
}
